// services/supabaseClient/unitService.js

import { v4 as uuid } from 'uuid';

import { supabase } from './supabaseClient';
const units_table_name = 'ml_units_staging';

/**
 * Fetch all units for a specific course
 * @param {string} courseId - The ID of the course to fetch units for
 * @returns {Promise<Object>} - { data: Unit[], error: any }
 */
export const fetchUnitsByCourseId = async (courseId) => {
  const { data, error } = await supabase
    .from(units_table_name)
    .select('*')
    .eq('course_id', courseId)
    .order('position', { ascending: true });
  return { data, error };
};

/**
 * Bulk insert units for a given course
 * @param {string} courseId - ID of the course
 * @param {string[]} units - List of unit titles
 * @returns {Promise<Object>} - { data: inserted units, error: any }
 */
export const insertUnitsForCourse = async (courseId, units) => {
  const formattedUnits = units.map((unit, index) => ({
    course_id: courseId,
    id: uuid(),
    title: typeof unit === 'string' ? unit : unit.title || unit.lessonTitle,
    position: unit.position ?? index,
    unit_objective: unit.objective || unit.unit_objective || '',
    unit_assessment: unit.assessment || unit.unit_assessment || '',
    current_page_index: 0, // Initialize at first page
  }));

  // eslint-disable-next-line no-console
  const { data, error } = await supabase.from(units_table_name).insert(formattedUnits).select();

  // eslint-disable-next-line no-console
  console.log('[insertUnitsForCourse] Insert result data:', data);
  if (error) {
    // eslint-disable-next-line no-console
    console.error('[insertUnitsForCourse] Insert error:', error);
  }
  return { data, error };
};

/**
 * Update the current page index for a specific unit
 * @param {string} unitId - ID of the unit to update
 * @param {number} pageIndex - Index of the current page/slide (0-based)
 * @returns {Promise<Object>} - { data, error }
 */
export const updateUnitPagePosition = async (unitId, pageIndex) => {
  const { data, error } = await supabase
    .from(units_table_name)
    .update({ current_page_index: pageIndex })
    .eq('id', unitId)
    .select()
    .single();

  return { data, error };
};

/**
 * Get the current page position for a specific unit
 * @param {string} unitId - ID of the unit
 * @returns {Promise<Object>} - { data: { current_page_index }, error }
 */
export const getUnitPagePosition = async (unitId) => {
  const { data, error } = await supabase
    .from(units_table_name)
    .select('current_page_index')
    .eq('id', unitId)
    .single();

  return { data, error };
};

export default {
  fetchUnitsByCourseId,
  insertUnitsForCourse,
  updateUnitPagePosition,
  getUnitPagePosition,
};
